import { Button } from "@admin/components/atoms/Button";
import { Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { Shift, UpdateShift, updateShift } from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface ClockInAgainModalProps extends ModalProps {
  shift?: Shift;
  companyId?: string;
  companyLocationId?: string;
}

export function ClockInAgainModal({
  shift,
  companyId,
  companyLocationId,
  ...props
}: ClockInAgainModalProps) {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<number>();
  const queryClient = useQueryClient();

  enum ClockInAgainModalFields {
    OnBreak = 1,
    OnWorking,
  }

  const handleClockInAgain = () => {
    if (!shift || !companyId || !companyLocationId || !shift.workEndDate)
      return;

    const updatedShift = {
      ...shift,
      companyId,
      companyLocationId,
    };

    if (selectedOption === ClockInAgainModalFields.OnBreak) {
      const durationInMilliseconds =
        new Date().getTime() - new Date(shift.workEndDate).getTime();
      const durationImMinutes = durationInMilliseconds / (1000 * 60);

      updatedShift.breakDuration = durationImMinutes;
    }

    if (selectedOption === ClockInAgainModalFields.OnWorking) {
      updatedShift.workEndDate = undefined;
    }

    mutation.mutate(updatedShift as UpdateShift);
  };

  const mutation = useMutation({
    mutationFn: updateShift,
    onSuccess: () => {
      toast.success(t("shift.Shift clocked in again successfully"));
      queryClient.invalidateQueries({
        queryKey: ["shifts", companyLocationId],
      });
      props.onClose?.();
    },
    onError: () => {
      toast.error(t("shift.Failed to clock in again"));
    },
  });

  const clockInAgainTexts = [
    {
      value: ClockInAgainModalFields.OnBreak,
      label: t("shift.Clock Break"),
    },
    {
      value: ClockInAgainModalFields.OnWorking,
      label: t("shift.Clock Working"),
    },
  ];

  return (
    <Modal
      title={`Clock ${shift?.firstName + " " + shift?.lastName} in Again`}
      {...props}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={props?.onClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            onClick={handleClockInAgain}
            disabled={!selectedOption}
            loading={mutation.isPending}
          >
            {t("shift.Clock In Again")}
          </Button>
        </>
      }
    >
      <form>
        <Label title={t("shift.Clock in Again Option")}>
          <Select
            variant="standart"
            onChange={(e) => setSelectedOption(Number(e.target.value))}
            options={clockInAgainTexts}
            placeholder={t("common.Select an option")}
          />
        </Label>
      </form>
    </Modal>
  );
}
